(() => {
  const ENHANCED = 'data-suri-select-enhanced';
  const NATIVE_OPT_OUT = 'data-suri-native-select';
  const MAX_MENU_HEIGHT = 260;
  const instances = new WeakMap();
  const activeInstances = new Set();
  let sequence = 0;

  const normalize = (value) => (value || '').replace(/\s+/g, ' ').trim();

  const splitOptionText = (option) => {
    const text = normalize(option.textContent);
    const explicitLabel = normalize(option.dataset.suriLabel);
    const explicitMeta = normalize(option.dataset.suriMeta);

    if (explicitLabel || explicitMeta) {
      return { value: explicitLabel || text, meta: explicitMeta };
    }

    const currency = /(?:[$€£¥₹₩₽₺₴₫₱₪₦₵]|\b(?:USD|CAD|AUD|NZD|EUR|GBP|JPY|CNY|HKD|SGD|INR|KRW|CHF|SEK|NOK|DKK|PLN|MXN|BRL|ZAR)\b)/i;
    const segments = text.split(/\s+(?:-|–|—)\s+/);
    const priceIndex = segments.findIndex((segment) => currency.test(segment));

    if (priceIndex > 0) {
      return {
        value: segments.slice(0, priceIndex).join(' - '),
        meta: segments.slice(priceIndex).join(' - '),
      };
    }

    const trailingPrice = text.match(
      /^(.*\S)\s+((?:[$€£¥₹₩₽₺₴₫₱₪₦₵]\s*)\d[\d.,]*(?:\s*[A-Z]{3})?|\d[\d.,]*\s*(?:USD|CAD|AUD|NZD|EUR|GBP|JPY|CNY|HKD|SGD|INR|KRW|CHF|SEK|NOK|DKK|PLN|MXN|BRL|ZAR))$/i,
    );

    return trailingPrice
      ? { value: trailingPrice[1], meta: trailingPrice[2] }
      : { value: text, meta: '' };
  };

  const createTextRow = (parts, className) => {
    const row = document.createElement('span');
    row.className = className;

    const value = document.createElement('span');
    value.className = 'suri-select__value';
    value.textContent = parts.value;
    row.append(value);

    if (parts.meta) {
      const meta = document.createElement('span');
      meta.className = 'suri-select__meta';
      meta.textContent = parts.meta;
      row.append(meta);
    }

    return row;
  };

  const createChevron = () => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.classList.add('suri-select__chevron');
    svg.setAttribute('viewBox', '0 0 7 5');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M1 1l2.5 2.5L6 1');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.append(path);

    return svg;
  };

  const close = (root, restoreFocus = false) => {
    if (!root?.classList.contains('is-open')) return;

    root.classList.remove('is-open', 'suri-select--up');
    root.closest('.video-popup')?.classList.remove('suri-select-open');
    const button = root.querySelector('.suri-select__button');
    const menu = root.querySelector('.suri-select__menu');
    button?.setAttribute('aria-expanded', 'false');
    menu?.setAttribute('hidden', '');
    menu?.querySelectorAll('.suri-select__option').forEach((item) => {
      item.tabIndex = -1;
    });

    if (restoreFocus) button?.focus();
  };

  const closeOthers = (current) => {
    document.querySelectorAll('.suri-select.is-open').forEach((root) => {
      if (root !== current) close(root);
    });
  };

  const enhance = (select) => {
    if (
      !(select instanceof HTMLSelectElement)
      || select.hasAttribute(NATIVE_OPT_OUT)
      || select.multiple
      || select.size > 1
    ) return;

    if (instances.has(select)) return;

    if (select.hasAttribute(ENHANCED)) {
      const staleCaret = select.nextElementSibling?.matches('svg')
        ? select.nextElementSibling
        : null;
      const staleProxy = (staleCaret || select).nextElementSibling;
      if (staleProxy?.classList.contains('suri-select')) staleProxy.remove();
      select.removeAttribute(ENHANCED);
      select.removeAttribute('aria-hidden');
      select.removeAttribute('tabindex');
    }

    const id = ++sequence;
    const root = document.createElement('div');
    const button = document.createElement('button');
    const selection = document.createElement('span');
    const chevron = createChevron();
    const menu = document.createElement('div');
    const buttonId = `SuriSelectButton-${id}`;
    const selectionId = `SuriSelectSelection-${id}`;
    const menuId = `SuriSelectMenu-${id}`;
    let selectedItem = null;
    let lastSignature = '';
    let instance;
    let typeahead = '';
    let typeaheadTimer;

    root.className = 'suri-select';
    root.dataset.suriSelectSource = select.id || select.name || String(id);

    button.type = 'button';
    button.id = buttonId;
    button.className = 'suri-select__button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', menuId);

    selection.id = selectionId;
    selection.className = 'suri-select__selection';
    button.append(selection, chevron);

    menu.id = menuId;
    menu.className = 'suri-select__menu';
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-labelledby', buttonId);
    menu.hidden = true;
    root.append(button, menu);

    const labels = Array.from(select.labels || []);
    const labelledBy = labels.map((label, index) => {
      if (!label.id || document.getElementById(label.id) !== label) {
        label.id = `SuriSelectLabel-${id}-${index + 1}`;
      }
      return label.id;
    });

    const nativeCaret = select.nextElementSibling?.matches('svg')
      ? select.nextElementSibling
      : null;
    (nativeCaret || select).insertAdjacentElement('afterend', root);
    select.setAttribute(ENHANCED, 'true');
    select.setAttribute('aria-hidden', 'true');
    select.tabIndex = -1;

    const focusItem = (item) => {
      if (!item) return;
      menu.querySelectorAll('.suri-select__option').forEach((option) => {
        option.tabIndex = option === item ? 0 : -1;
      });
      item.focus({ preventScroll: true });
      item.scrollIntoView({ block: 'nearest' });
    };

    const enabledItems = () => Array.from(
      menu.querySelectorAll('.suri-select__option:not(:disabled)'),
    );

    const syncAttribute = (name, value) => {
      if (value == null || value === '') button.removeAttribute(name);
      else button.setAttribute(name, value);
    };

    const syncAria = (selectedParts) => {
      const explicitLabelledBy = normalize(select.getAttribute('aria-labelledby'))
        .split(' ')
        .filter(Boolean);
      const references = Array.from(new Set([
        ...explicitLabelledBy,
        ...labelledBy,
      ]));
      const explicitLabel = normalize(select.getAttribute('aria-label'));
      const selectedText = normalize(`${selectedParts.value} ${selectedParts.meta}`);

      if (references.length) {
        button.setAttribute('aria-labelledby', [...references, selectionId].join(' '));
        syncAttribute('aria-label', explicitLabel || null);
      } else if (explicitLabel) {
        button.removeAttribute('aria-labelledby');
        button.setAttribute(
          'aria-label',
          selectedText ? `${explicitLabel}: ${selectedText}` : explicitLabel,
        );
      } else {
        button.setAttribute('aria-labelledby', selectionId);
        button.removeAttribute('aria-label');
      }

      syncAttribute('aria-describedby', select.getAttribute('aria-describedby'));
      syncAttribute('aria-errormessage', select.getAttribute('aria-errormessage'));
      syncAttribute('aria-invalid', select.getAttribute('aria-invalid'));
      syncAttribute('aria-required', select.required ? 'true' : null);
    };

    const stateSignature = () => {
      const selected = select.options[select.selectedIndex];
      return [
        select.selectedIndex,
        select.value,
        select.disabled,
        select.hidden,
        select.required,
        select.options.length,
        selected?.textContent || '',
        getComputedStyle(select).display,
      ].join('|');
    };

    const choose = (option) => {
      if (option.disabled || option.parentElement?.disabled) return;
      if (select.selectedIndex === option.index) {
        render();
        close(root, true);
        return;
      }
      select.selectedIndex = option.index;
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
      render();
      close(root, true);
    };

    const createOption = (option, inheritedDisabled = false) => {
      if (option.hidden) return null;

      const item = document.createElement('button');
      const isSelected = option.index === select.selectedIndex;
      item.type = 'button';
      item.className = 'suri-select__option';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', String(isSelected));
      item.dataset.value = option.value;
      item.dataset.index = String(option.index);
      item.disabled = inheritedDisabled || option.disabled;
      item.tabIndex = -1;
      item.append(createTextRow(splitOptionText(option), 'suri-select__row'));
      item.addEventListener('click', () => choose(option));
      if (isSelected) selectedItem = item;
      return item;
    };

    function render() {
      const selected = select.options[select.selectedIndex];
      const selectedParts = selected
        ? splitOptionText(selected)
        : { value: '', meta: '' };
      selection.replaceChildren(...createTextRow(selectedParts, 'suri-select__selection').childNodes);
      syncAria(selectedParts);
      root.hidden = select.hidden || getComputedStyle(select).display === 'none';
      selectedItem = null;

      const fragment = document.createDocumentFragment();
      Array.from(select.children).forEach((child) => {
        if (child instanceof HTMLOptGroupElement) {
          if (child.hidden) return;
          const group = document.createElement('div');
          group.className = 'suri-select__group';
          group.setAttribute('role', 'presentation');
          group.textContent = child.label;
          fragment.append(group);
          Array.from(child.children).forEach((option) => {
            const item = createOption(option, child.disabled);
            if (item) fragment.append(item);
          });
          return;
        }

        if (child instanceof HTMLOptionElement) {
          const item = createOption(child);
          if (item) fragment.append(item);
        }
      });

      menu.replaceChildren(fragment);
      const hasEnabledOption = enabledItems().length > 0;
      button.disabled = select.disabled || !hasEnabledOption;
      root.classList.toggle('is-disabled', button.disabled);
      lastSignature = stateSignature();
    }

    const placeMenu = () => {
      root.classList.remove('suri-select--up');
      const rect = root.getBoundingClientRect();
      const needed = Math.min(menu.scrollHeight, MAX_MENU_HEIGHT);
      const roomBelow = window.innerHeight - rect.bottom;
      const roomAbove = rect.top;
      if (roomBelow < needed && roomAbove > roomBelow) {
        root.classList.add('suri-select--up');
      }
    };

    const open = (focus = 'selected') => {
      if (button.disabled) return;
      render();
      closeOthers(root);
      root.classList.add('is-open');
      root.closest('.video-popup')?.classList.add('suri-select-open');
      button.setAttribute('aria-expanded', 'true');
      menu.removeAttribute('hidden');
      placeMenu();

      const items = enabledItems();
      const target = focus === 'first'
        ? items[0]
        : focus === 'last'
          ? items[items.length - 1]
          : selectedItem && !selectedItem.disabled
            ? selectedItem
            : items[0];
      requestAnimationFrame(() => focusItem(target));
    };

    const moveFocus = (direction) => {
      const items = enabledItems();
      if (!items.length) return;
      const currentIndex = items.indexOf(document.activeElement);
      const nextIndex = currentIndex < 0
        ? 0
        : Math.max(0, Math.min(items.length - 1, currentIndex + direction));
      focusItem(items[nextIndex]);
    };

    const focusAdjacentControl = (backwards) => {
      const selector = [
        'a[href]',
        'area[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');
      const controls = Array.from(document.querySelectorAll(selector)).filter((control) => (
        !menu.contains(control)
        && !control.hidden
        && control.getClientRects().length > 0
      ));
      const currentIndex = controls.indexOf(button);
      const target = controls[currentIndex + (backwards ? -1 : 1)];
      close(root);
      (target || button).focus();
    };

    button.addEventListener('click', () => {
      if (root.classList.contains('is-open')) close(root, true);
      else open();
    });

    button.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        open('selected');
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        open('selected');
      } else if (event.key === 'Home') {
        event.preventDefault();
        open('first');
      } else if (event.key === 'End') {
        event.preventDefault();
        open('last');
      }
    });

    menu.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close(root, true);
        return;
      }

      if (event.key === 'Tab') {
        event.preventDefault();
        focusAdjacentControl(event.shiftKey);
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        moveFocus(event.key === 'ArrowDown' ? 1 : -1);
        return;
      }

      const items = enabledItems();
      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        focusItem(event.key === 'Home' ? items[0] : items[items.length - 1]);
        return;
      }

      if (
        event.key.length === 1
        && !event.altKey
        && !event.ctrlKey
        && !event.metaKey
      ) {
        typeahead += event.key.toLocaleLowerCase();
        window.clearTimeout(typeaheadTimer);
        typeaheadTimer = window.setTimeout(() => { typeahead = ''; }, 500);
        const match = items.find((item) => normalize(
          item.querySelector('.suri-select__value')?.textContent,
        ).toLocaleLowerCase().startsWith(typeahead));
        if (match) focusItem(match);
      }
    });

    const form = select.form;
    const focusHandler = () => button.focus();
    const resetHandler = () => requestAnimationFrame(render);
    select.addEventListener('focus', focusHandler);
    select.addEventListener('input', render);
    select.addEventListener('change', render);
    form?.addEventListener('reset', resetHandler);

    const observer = new MutationObserver(render);
    observer.observe(select, {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true,
    });

    const cleanup = () => {
      window.clearTimeout(typeaheadTimer);
      observer.disconnect();
      select.removeEventListener('focus', focusHandler);
      select.removeEventListener('input', render);
      select.removeEventListener('change', render);
      form?.removeEventListener('reset', resetHandler);
      root.remove();
      activeInstances.delete(instance);
      instances.delete(select);
    };

    const sync = () => {
      if (!select.isConnected) {
        cleanup();
        return;
      }
      if (stateSignature() !== lastSignature) render();
    };

    instance = { root, render, observer, sync, cleanup };
    instances.set(select, instance);
    activeInstances.add(instance);
    render();
  };

  const scan = (scope = document) => {
    if (scope instanceof HTMLSelectElement) enhance(scope);
    if (scope.querySelectorAll) scope.querySelectorAll('select').forEach(enhance);
  };

  document.addEventListener('click', (event) => {
    const clickedDropdown = event.composedPath().some((node) => (
      node instanceof Element && node.classList.contains('suri-select')
    ));
    if (!clickedDropdown) closeOthers(null);
  });

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  window.addEventListener('resize', () => closeOthers(null), { passive: true });
  window.addEventListener('pageshow', () => {
    activeInstances.forEach((instance) => instance.sync());
  });
  window.setInterval(() => {
    activeInstances.forEach((instance) => instance.sync());
  }, 250);

  const start = () => {
    scan();
    new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node instanceof Element) scan(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
